import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LayoutGrid, LayoutGridItem } from '../../../../../layout/LayoutGrid';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GridColumns } from '@mui/x-data-grid-pro';
import TimelineIcon from '@mui/icons-material/Timeline';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { xGridDateSort } from 'app/components/Common/XGrid/sorting/XGridSorting';
import { XGridChangeHistoryOverflowCell } from 'app/components/Common/XGrid/cells/XGridChangeHistoryOverflowCell';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { facilityActions } from '@AMIEWEB/Facility/FacilityStore/Facility.redux';
import { selectFacilityChangeHistory } from '@AMIEWEB/Facility/FacilityStore/Facility.selector';
import { useParams } from 'react-router-dom';
import { StrictXGridHistoricalHoverView } from '@AMIEWEB/Common/XGrid/cells/HoverViewRenderers/XGridStrictHoverViewContent ';
import { ChangeHistoryTimelineModal } from '@AMIEWEB/Common/ChangeHistoryTimeline';
import type { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';

const useStyles = makeStyles()({
  container: {
    padding: '24px 0px',
  },
});


export const changeHistoryColumns = (t): GridColumns => [
  {
    ...XGridDefaultColumn,
    field: 'fieldChanged',
    headerName: t('order.changeHistoryTab.grid.field'),
  },
  {
    ...XGridDefaultColumn,
    field: 'oldValue',
    headerName:  t('order.changeHistoryTab.grid.oldValue'),
    renderCell: XGridChangeHistoryOverflowCell(StrictXGridHistoricalHoverView)(),
    sortable: false,
  },
  {
    ...XGridDefaultColumn,
    field: 'newValue',
    headerName:  t('order.changeHistoryTab.grid.newValue'),
    renderCell: XGridChangeHistoryOverflowCell(StrictXGridHistoricalHoverView)(),
    sortable: false,
  },
  {
    ...XGridDefaultColumn,
    field: 'userName',
    headerName:  t('order.changeHistoryTab.grid.changedBy'),
  },
  {
    ...XGridDefaultColumn,
    field: 'created',
    headerName:  t('order.changeHistoryTab.grid.date'),
    type: 'date',
    sortComparator: xGridDateSort,
  },
  {
    ...XGridDefaultColumn,
    field: 'externalSystem',
    headerName:  t('order.changeHistoryTab.grid.sourceSystem'),
    sortable: false,
  },
];

export const ChangeHistory = () => {
  const { id: facilityId } = useParams<{ id: string }>();

  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { promiseInProgress } = usePromiseTracker({ area: 'facility-change-history' });
  
  const rows = useSelector(selectFacilityChangeHistory) ?? [];
  const [columns, setColumns] = useState<GridColumns>(changeHistoryColumns(t));
  const [sortModel, setSortModel] = React.useState<any>([
    {
      field: 'created',
      sort: 'desc',
    },
  ]);
  const [timelineModalOpen, setTimelineModalOpen] = React.useState(false);

  const rightActions: IXGridBulkActionButton[] = React.useMemo(() => {
    if (!rows?.length) return [];
    return [
      {
        KeyId: 'timeline-visualization',
        Icon: TimelineIcon,
        tooltipProps: {
          tooltipContent: t('order.changeHistoryTab.timelineVisualization'),
          disabled: false,
        },
        onClick: () => setTimelineModalOpen(true),
      },
    ];
  }, [rows?.length, t]);

  const fetchFacilityChangeHistoryData = () => {
    dispatch(facilityActions.getFacilityChangeHistory({ facilityId: Number(facilityId), pageSize: 1000, pageNumber: 1 }));
  };

  React.useEffect(() => {
    fetchFacilityChangeHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutGrid>
      <LayoutGridItem xs={12}>
        <Box className={classes.container}>
          <XGrid
            columns={columns}
            rows={rows}
            headerHeight={45}
            autoHeight
            disableSelectionOnClick
            loading={promiseInProgress}
            sortModel={sortModel}
            onSortModelChange={model => setSortModel(model)}
            toolbarProps={{
              refreshButton: true,
              refreshGrid: () => fetchFacilityChangeHistoryData(),
              resetColumns: () => setColumns(changeHistoryColumns(t)),
              rightActions,
            }}
          />
          <ChangeHistoryTimelineModal
            open={timelineModalOpen}
            onClose={() => setTimelineModalOpen(false)}
            title={t('facility.changeHistoryTab.timelineModalTitle')}
            historyData={rows}
          />
        </Box>
      </LayoutGridItem>
    </LayoutGrid>
  );
};
